import { FC, useMemo } from 'react';

import { HeroImage } from '../../types/Franchises';
import { StoryblokVideo } from '../../types/Vimeo';
import { AspectRatio } from '../AspectRatio/AspectRatio';
import { Image } from '../Image';
import PageSection from '../Layout/PageSection';
import { Hero } from '../Posts';
import { SpotifyPlayer } from '../Spotify/SpotifyPlayer';
import { LightboxVideoPlayer } from './LightboxVideoPlayer';
import { VideoPlayer } from './VideoPlayer';

interface JournalsHeroProps {
  author?: string;
  handleShare: (platform: string) => void;
  handleVideoPlay: () => void;
  media: HeroImage | Spotify | StoryblokVideo | Youtube;
  publishedAt: string | null;
  readingTime: number;
  subtitle: string;
  title: string;
  variant: 'aside' | 'below' | 'immersive';
}

export const JournalsHero: FC<JournalsHeroProps> = ({
  author,
  handleShare,
  handleVideoPlay,
  media,
  publishedAt,
  readingTime,
  subtitle,
  title,
  variant = 'aside',
}) => {
  const image = media?.component === 'image' && media.image;
  const spotify = media?.component === 'spotify' && media.playlistId;
  const video = useMemo(() => {
    switch (media?.component) {
      case 'Video':
        return {
          baseUrl: 'https://player.vimeo.com/video/',
          poster: media.coverImage.filename,
          videoId: media.vimeo?.vimeo_oembed?.response?.video_id,
        };
      case 'youtube':
        return {
          baseUrl: 'https://www.youtube.com/embed/',
          poster: media.cover.filename,
          ratio: media.ratio,
          videoId: media.videoId,
        };
      default:
        return null;
    }
  }, [media]);

  const header = (
    <Hero
      buttonLocation="story"
      handleShare={handleShare}
      publishedAt={publishedAt}
      readingTime={readingTime}
      subtitle={subtitle}
      title={title}
      writtenBy={author}
    />
  );

  if (variant === 'immersive') {
    return (
      <div className="relative">
        {image ? (
          <figure className="flex-1">
            <Image
              alt={image.alt || title}
              eagerLoading
              focus={image.focus}
              size={{
                sm: { span: 12, ratio: '4/3' },
                md: { span: 8, ratio: '3/2' },
                lg: { span: 12, ratio: '2/1' },
              }}
              src={image.filename}
            />
          </figure>
        ) : (
          <AspectRatio lg="2/1" md="3/2" sm="4/3">
            {video && (
              <LightboxVideoPlayer
                {...video}
                handlePlay={handleVideoPlay}
                title={title}
                videoType={media?.component === 'youtube' ? 'YOUTUBE' : 'VIMEO'}
              />
            )}
          </AspectRatio>
        )}

        <div className="relative bottom-0 z-20 w-full md:-mt-20">
          <div className="mx-auto w-full max-w-2xl bg-white p-9 md:rounded-t-lg md:p-12">
            {header}
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'below') {
    return (
      <PageSection>
        <div className="flex flex-col gap-8 py-9 md:py-14">
          {header}
          {image && (
            <figure className="flex-1">
              <Image
                alt={image.alt || title}
                eagerLoading
                focus={image.focus}
                shape="rounded"
                size={{
                  sm: { span: 12, ratio: '1/1' },
                  md: { span: 8, ratio: '16/9' },
                  lg: { span: 12, ratio: '16/9' },
                }}
                src={image.filename}
              />
            </figure>
          )}
          {video && (
            <VideoPlayer
              {...video}
              handlePlay={handleVideoPlay}
              ratio={{
                sm: '16/9',
              }}
              title={title}
            />
          )}
          {spotify && (
            <SpotifyPlayer
              className="mx-auto w-full max-w-3xl rounded-lg"
              id={spotify}
            />
          )}
        </div>
      </PageSection>
    );
  }

  return (
    <PageSection>
      <div className="grid-section py-9 md:py-14">
        <div className="col-span-12 md:col-span-6 lg:col-span-5">{header}</div>
        <div className="col-span-12 mt-6 md:col-span-6 md:mt-0 lg:col-span-7">
          {image && (
            <figure className="w-full">
              <Image
                alt={image.alt || title}
                eagerLoading
                focus={image.focus}
                shape="rounded"
                size={{
                  sm: { span: 12, ratio: '3/2' },
                  md: { span: 6, ratio: '1/1' },
                  lg: { span: 6, ratio: '1/1' },
                }}
                src={image.filename}
              />
            </figure>
          )}
          {video && (
            <figure className="mx-auto w-full max-w-sm lg:max-w-lg">
              <VideoPlayer
                {...video}
                handlePlay={handleVideoPlay}
                ratio={{
                  sm: '9/16',
                  md: '9/16',
                  lg: '9/16',
                }}
                title={title}
              />
            </figure>
          )}
        </div>
      </div>
    </PageSection>
  );
};
